<template>
  <div class="blog-form">
    <!-- Title input -->
    <div class="form-group">
      <label for="article-title">Názov článku</label>
      <input
        type="text"
        id="article-title"
        class="form-input"
        placeholder="Zadajte názov článku"
        v-model="article.nazov"
      />
    </div>

    <!-- Slug input -->
    <div class="form-group">
      <div class="slug-container">
        <div class="slug-label">Odkaz na článok bude vyzerať nasledovne:</div>
        <div class="slug-input-group">
          <span class="slug-prefix">www.doffero.com/blog/</span>
          <input 
            type="text" 
            class="slug-input" 
            v-model="article.slug"
            :style="{ width: getSlugWidth + 'px' }"
          />
        </div>
      </div>
    </div>

    <!-- Perex textarea -->
    <div class="form-group">
      <label for="article-perex" :class="{ 'error': errors.perex }">Perex</label>
      <textarea
        id="article-perex"
        class="form-textarea"
        :class="{ 'error': errors.perex }"
        placeholder="Zadajte krátky popis článku"
        v-model="article.perex"
        rows="5"
      ></textarea>
    </div>

    <!-- Date picker -->
    <div class="form-group">
      <label for="article-date">Dátum publikacie</label>
      <date-picker
        id="article-date"
        type="date"
        v-model="article.datum"
        format="DD.MM.YYYY"
        placeholder="dd.mm.rrrr"
        valueType="format"
        class="date-picker"
      ></date-picker>
    </div>

    <!-- Image upload -->
    <div class="form-group">
      <label class="upload-label">FOTKA / OBRÁZOK</label>
      <div class="upload-container" ref="uploadContainer">
        <input 
          type="file" 
          ref="fileInput" 
          class="file-input" 
          accept=".jpg,.png,.svg"
          @change="handleFileChange"
        />
        <div class="upload-content" @click="triggerFileInput">
          <div class="upload-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="upload-text">
            Vlož <b>fotku</b> alebo <b>obrázok</b> presunutím alebo <span class="upload-link">nahraj obrázok</span>
          </div>
        </div>
        <div v-if="article.obrazok" class="image-preview">
          <div class="image-preview-content">
            <img :src="previewImage" alt="Náhľad obrázku" class="preview-img" />
            <div class="image-info">
              <span class="image-title">{{ getImageName }}</span>
            </div>
            <button class="remove-image" @click="removeImage">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Rich text editor -->
    <div class="form-group editor-container">
      <vue-editor v-model="article.obsah" :editor-toolbar="customToolbar"></vue-editor>
    </div>

    <!-- Action buttons -->
    <div class="form-actions">
      <button 
        class="action-button" 
        :class="{ 'loading': saved }" 
        @click="createArticle"
      >
        {{ saved ? "Tvorba sitemapy...." : article.id ? 'Uložiť' : 'Vytvoriť' }}
      </button>
    </div>
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import { VueEditor } from "vue2-editor"
import axios from 'axios'

export default {
  props: {
    editableArticel: {
      type: Object,
      default: null
    },
    saved: {
      type: Boolean,
      default: false
    }
  },
  
  components: {
    VueEditor,
    DatePicker
  },
  
  data() {
    return {
      article: {
        nazov: null,
        perex: null,
        slug: null,
        obsah: null,
        obrazok: null,
        datum: null,
        id: null
      },
      errors: {},
      customToolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ["bold", "italic", "underline", "strike"], // toggled buttons
        [
          { align: "" },
          { align: "center" },
          { align: "right" },
          { align: "justify" }
        ],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        ["link", "image", "video"],
        ["clean"] // remove formatting button
      ],
      previewImage: null
    }
  },
  
  computed: {
    getSlugWidth() {
      return this.article.slug ? Math.max(50, this.article.slug.length * 10) : 50
    },
    getImageName() {
      if (!this.article.obrazok) return '';
      
      if (this.article.obrazok instanceof File) {
        return this.article.obrazok.name;
      } else if (typeof this.article.obrazok === 'string') {
        try {
          // Try to parse the JSON string
          const imageData = JSON.parse(this.article.obrazok)
          if (Array.isArray(imageData) && imageData.length > 0) {
            return imageData[0].name || 'Uploaded image'
          }
        } catch (e) {
          // If it's not JSON, return a default name
          return 'Uploaded image'
        }
      } else if (typeof this.article.obrazok === 'object' && this.article.obrazok !== null) {
        return this.article.obrazok.name || this.article.obrazok.file || 'Uploaded image'
      }
      
      return 'Uploaded image'
    }
  },
  
  created() {
    if (this.editableArticel) {
      this.article = { ...this.editableArticel }
      if (this.article.obrazok) {
        this.generatePreviewImage()
      }
    }
  },
  
  methods: {
    createArticle() {
      this.$emit('createArticle', this.article)
    },
    
    saveSlug(value) {
      return value
        .toString()
        .toLowerCase()
        .replace(/\s+/g, "-") // Replace spaces with -
        .replace(/[^\w\-]+/g, "") // Remove all non-word chars
        .replace(/\-\-+/g, "-") // Replace multiple - with single -
        .replace(/^-+/, "") // Trim - from start of text
        .replace(/-+$/, "") // Trim - from end of text
    },
    
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    
    handleFileChange(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/svg+xml']
      if (!allowedTypes.includes(file.type)) {
        this.showError('Iba JPG, PNG a SVG obrázky môžu byť nahranté.')
        return
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.showError(`${file.name} je príliš velký! Prosím, výberte soubor do velikosti 5 MB.`)
        return
      }
      
      this.article.obrazok = file
      this.generatePreviewImage()
    },
    
    generatePreviewImage() {
      if (this.article.obrazok instanceof File) {
        this.previewImage = URL.createObjectURL(this.article.obrazok)
      } else if (typeof this.article.obrazok === 'string') {
        try {
          // Try to parse the JSON string
          const imageData = JSON.parse(this.article.obrazok)
          if (Array.isArray(imageData) && imageData.length > 0) {
            this.previewImage = imageData[0].file || imageData[0].data?.url
          }
        } catch (e) {
          // If it's not JSON, use the string directly
          this.previewImage = this.article.obrazok
        }
      } else if (typeof this.article.obrazok === 'object' && this.article.obrazok !== null) {
        this.previewImage = this.article.obrazok.file || this.article.obrazok.url || null
      }
    },
    
    removeImage() {
      if (this.previewImage && this.article.obrazok instanceof File) {
        URL.revokeObjectURL(this.previewImage)
      }
      
      this.previewImage = null
      this.article.obrazok = null
      this.$refs.fileInput.value = ''
      
      if (this.article.id) {
        // If we have an article ID, notify the server to remove the image
        axios.post('/api/blog/removeImage', { id: this.article.id })
          .then(response => {
            // Handle success if needed
          })
          .catch(error => {
            console.error('Error removing image:', error)
          })
      }
    },
    
    showError(message) {
      // Use the toast system if available
      if (this.$toast) {
        this.$toast.warning(message, {
          position: "top-right",
          timeout: 5000,
          closeOnClick: true,
          pauseOnFocusLoss: true,
          pauseOnHover: true,
          draggable: true,
          draggablePercent: 0.6,
          showCloseButtonOnHover: false,
          hideProgressBar: false,
          closeButton: "button",
          icon: true,
          rtl: false
        })
      } else {
        alert(message)
      }
    }
  },
  
  watch: {
    "article.nazov": function(newVal) {
      // Uncomment the line below if you want to auto-generate slug from title
      // this.article.slug = this.saveSlug(newVal);
    }
  }
}
</script>

<style lang="scss" scoped>
.blog-form {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Roboto', sans-serif;
  
  .form-group {
    margin-bottom: 1.5rem;
    width: 100%;
  }
  
  label {
    display: block;
    font-weight: bold;
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #9598A7;
    margin-bottom: 0.5rem;
    
    &.error {
      color: #F3385A;
    }
  }
  
  .form-input, .form-textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    background: #FFFFFF;
    border: 1px solid #DDE3E8;
    border-radius: 4px;
    font-size: 14px;
    line-height: 1.5;
    color: #212541;
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #2ED8B6;
    }
    
    &.error {
      border-color: #F3385A;
    }
  }
  
  .form-textarea {
    resize: vertical;
    min-height: 150px;
  }
  
  .slug-container {
    margin-bottom: 1rem;
    
    .slug-label {
      font-size: 14px;
      line-height: 1.5;
      color: #404040;
      margin-bottom: 0.5rem;
    }
    
    .slug-input-group {
      display: flex;
      align-items: center;
      
      .slug-prefix {
        font-size: 14px;
        color: #212541;
      }
      
      .slug-input {
        font-weight: bold;
        font-size: 14px;
        color: #212541;
        background: transparent;
        border: none;
        border-bottom: 2px solid #212541;
        padding: 4px 0;
        transition: all 0.2s ease;
        
        &:focus {
          outline: none;
          border-color: #2ED8B6;
        }
      }
    }
  }
  
  .date-picker {
    width: 100%;
    
    :deep(.mx-input) {
      height: 42px;
      font-size: 14px;
      color: #212541;
      border: 1px solid #DDE3E8;
      border-radius: 4px;
      
      &:focus {
        border-color: #2ED8B6;
        box-shadow: none;
      }
    }
  }
  
  .upload-label {
    margin-bottom: 0.75rem;
  }
  
  .upload-container {
    position: relative;
    border: 1px dashed #DDE3E8;
    border-radius: 4px;
    padding: 1.5rem;
    background-color: #FFFFFF;
    transition: border-color 0.2s ease;
    
    &:hover {
      border-color: #2ED8B6;
    }
    
    .file-input {
      position: absolute;
      width: 0.1px;
      height: 0.1px;
      opacity: 0;
      overflow: hidden;
      z-index: -1;
    }
    
    .upload-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      
      .upload-icon {
        margin-bottom: 1rem;
        color: #9598A7;
      }
      
      .upload-text {
        font-size: 14px;
        line-height: 1.5;
        color: #263544;
        text-align: center;
        
        .upload-link {
          font-weight: 600;
          color: #2ED8B6;
          text-decoration: underline;
        }
      }
    }
    
    .image-preview {
      margin-top: 1rem;
      
      .image-preview-content {
        display: flex;
        align-items: center;
        padding: 0.75rem;
        background-color: #f8f9fa;
        border-radius: 4px;
        
        .preview-img {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 4px;
        }
        
        .image-info {
          flex: 1;
          margin-left: 1rem;
          
          .image-title {
            display: block;
            font-size: 14px;
            font-weight: 500;
            color: #212541;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 200px;
          }
        }
        
        .remove-image {
          width: 36px;
          height: 36px;
          border-radius: 4px;
          background: transparent;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #F3385A;
          transition: background-color 0.2s ease;
          margin-left: 0.5rem;
          
          &:hover {
            background: rgba(243, 56, 90, 0.1);
          }
        }
      }
    }
  }
  
  .editor-container {
    margin-top: 2rem;
    
    :deep(.quillWrapper) {
      width: 100% !important;
      
      .ql-toolbar.ql-snow {
        border-top-left-radius: 4px;
        border-top-right-radius: 4px;
        background: white !important;
        border: 1px solid #DDE3E8 !important;
      }
      
      .ql-container.ql-snow {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        background: white !important;
        border: 1px solid #DDE3E8 !important;
        border-top: 0 !important;
        
        .ql-editor {
          min-height: 300px !important;
          font-size: 14px;
          line-height: 1.6;
          
          &.ql-blank::before {
            font-style: italic;
            color: #9598A7;
          }
        }
      }
    }
  }
  
  .form-actions {
    margin-top: 3rem;
    display: flex;
    justify-content: center;
    
    .action-button {
      width: 200px;
      height: 55px;
      background: #2ED8B6;
      border: none;
      border-radius: 4px;
      font-weight: bold;
      font-size: 16px;
      color: #FFFFFF;
      cursor: pointer;
      transition: background-color 0.2s ease;
      
      &:hover {
        background: #26c0a1;
      }
      
      &.loading {
        opacity: 0.8;
        cursor: wait;
      }
    }
  }
}

@media (max-width: 768px) {
  .blog-form {
    padding: 0 1rem;
    
    .form-actions {
      .action-button {
        width: 100%;
      }
    }
  }
}
</style>